/**
 * Months data (docs/04-api.md "Months"; docs/06-roadmap.md P1-05/P1-06). Household- and
 * period-scoped query keys so switching either invalidates cleanly (docs/03-architecture.md §4.3).
 *
 * Payment writes keep confirmed amounts visible until the server accepts the change.
 * Failure preserves the previous record and exposes a retryable error.
 */
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BucketDef, MonthAnalysis } from '@finbob/shared';
import { api, ApiError } from '@/lib/api-client';
import { useToast } from '@/stores/toast.store';
import { acquireLineEdit } from './line-edit-queue';

export type LineKind = 'income' | 'expense';
export type LineStatus = 'unpaid' | 'paid' | 'skipped';
export type EditScope = 'this_month' | 'this_and_future';

export interface LineSource {
  type: 'manual' | 'recurring' | 'credit_card' | 'carry_over';
  kinds?: string[];
  cadence?: string[];
  classification?: string;
  cardAccountId?: string;
}

export interface LineLink {
  type: 'loan' | 'deposit' | 'goal' | 'hand_loan';
  loanId?: string;
  kind?: 'emi' | 'prepayment';
  mode?: 'reduce_tenure' | 'reduce_emi';
  depositId?: string;
  goalId?: string;
  handLoanId?: string;
}

export interface MonthLine {
  computed?: {
    detail?: {
      statementId?: string;
      statementPeriod?: string;
      statementBalance?: number;
      remainingBillDue?: number;
      netSpending?: number;
      recordedNetSpending?: number;
      paymentBasis?: 'net_recorded_spending' | 'estimated_reserve';
      currentOutstanding?: number;
      pendingDebits?: number;
      appliedAfterCutoff?: number;
      unknownCutoffCount?: number;
      estimated?: boolean;
      subscriptions?: {
        id: string;
        name: string;
        cadence: string;
        amount: number;
        dueDate?: string;
      }[];
    };
  };
  recurringEventKey?: string;
  borrowingEventKey?: string;
  investmentEventKey?: string;
  id: string;
  templateLineId?: string;
  kind: LineKind;
  bucketKey?: string;
  section: string;
  subsection?: string;
  name: string;
  personId?: string;
  accountId?: string;
  plannedAmount: number;
  actualAmount?: number;
  status: LineStatus;
  paidAt?: string;
  paidBy?: string;
  note?: string;
  source: LineSource;
  link?: LineLink;
  dueDay?: number;
  reminderDaysBefore?: number[];
  overridden: boolean;
  sortOrder: number;
}

export interface MonthSummaryRow {
  period: string;
  status: 'open' | 'closed';
  summary?: { buckets: BucketDef[]; closedBy: string };
}

export interface Month {
  closeBlockers?: string[];
  id: string;
  period: string;
  status: 'open' | 'closed';
  upcoming: boolean;
  openedAt: string;
  setupRequired: boolean;
  setupCompletedAt?: string;
  closedAt?: string;
  carryOverIn: number;
  buckets: BucketDef[];
  lines: MonthLine[];
  analysis: MonthAnalysis;
}

function monthsListKey(hid: string) {
  return ['h', hid, 'months'] as const;
}

function monthKey(hid: string, period: string) {
  return ['h', hid, 'months', period] as const;
}

export function useMonths(hid: string) {
  return useQuery({
    queryKey: monthsListKey(hid),
    queryFn: () => api.get<MonthSummaryRow[]>(`/households/${hid}/months`),
  });
}

export function useMonth(hid: string, period: string) {
  return useQuery({
    queryKey: monthKey(hid, period),
    queryFn: () => api.get<Month>(`/households/${hid}/months/${period}`),
    retry: (failureCount, err) => err instanceof ApiError && err.status !== 404 && failureCount < 2,
  });
}

export function useOpenMonth(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Month opened' },
    mutationFn: () => api.post<Month>(`/households/${hid}/months/${period}/open`),
    onSuccess: (month) => {
      queryClient.setQueryData(monthKey(hid, period), month);
      void queryClient.invalidateQueries({ queryKey: monthsListKey(hid) });
    },
  });
}

export function useCompleteMonthSetup(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Monthly setup completed' },
    mutationFn: () => api.post<Month>(`/households/${hid}/months/${period}/setup/complete`),
    onSuccess: (month) => queryClient.setQueryData(monthKey(hid, period), month),
  });
}

export function useDeleteMonthSetup(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Monthly setup removed' },
    mutationFn: () => api.delete<void>(`/households/${hid}/months/${period}`),
    onSuccess: () => {
      void queryClient.resetQueries({ queryKey: monthKey(hid, period) });
      void queryClient.invalidateQueries({ queryKey: monthsListKey(hid) });
    },
  });
}

export function useCloseMonth(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Month closed' },
    mutationFn: () =>
      api.post<{ unpaidCount: number }>(`/households/${hid}/months/${period}/close`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: monthKey(hid, period) });
      void queryClient.invalidateQueries({ queryKey: monthsListKey(hid) });
    },
  });
}

export function useReopenMonth(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Month reopened' },
    mutationFn: () => api.post<Month>(`/households/${hid}/months/${period}/reopen`),
    onSuccess: (month) => {
      queryClient.setQueryData(monthKey(hid, period), month);
      void queryClient.invalidateQueries({ queryKey: monthsListKey(hid) });
    },
  });
}

export interface AddLineInput {
  kind: LineKind;
  bucketKey?: string;
  section: string;
  subsection?: string;
  name: string;
  personId?: string;
  accountId?: string;
  plannedAmount: number;
  dueDay?: number;
  addToTemplate: boolean;
}

export function useAddLine(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Entry added' },
    mutationFn: (input: AddLineInput) =>
      api.post<MonthLine>(`/households/${hid}/months/${period}/lines`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: monthKey(hid, period) });
    },
  });
}

export interface PatchLineChanges {
  bucketKey?: string;
  section?: string;
  subsection?: string;
  name?: string;
  personId?: string;
  accountId?: string;
  plannedAmount?: number;
  note?: string;
  dueDay?: number;
}

export function usePatchLine(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...monthKey(hid, period), 'line-edit'],
    meta: { successMessage: 'Entry updated' },
    mutationFn: ({
      lineId,
      changes,
      scope,
    }: {
      lineId: string;
      changes: PatchLineChanges;
      scope: EditScope;
    }) =>
      api.patch<MonthLine>(`/households/${hid}/months/${period}/lines/${lineId}`, {
        changes,
        scope,
      }),
    onMutate: async ({ lineId, changes, scope }) => {
      const finish = await acquireLineEdit(queryClient, hid, period, lineId);
      const key = monthKey(hid, period);
      try {
        await queryClient.cancelQueries({ queryKey: key });
      } catch (error) {
        finish();
        throw error;
      }
      const previous = queryClient.getQueryData<Month>(key);
      const original = previous?.lines.find((line) => line.id === lineId);
      const index = previous?.lines.findIndex((line) => line.id === lineId) ?? -1;
      if (previous)
        queryClient.setQueryData<Month>(key, {
          ...previous,
          lines: previous.lines.map((line) =>
            line.id === lineId
              ? {
                  ...line,
                  ...Object.fromEntries(
                    Object.entries(changes).filter(([, value]) => value !== undefined),
                  ),
                  overridden: scope === 'this_month',
                }
              : line,
          ),
        });
      return { original, index, finish };
    },
    onError: (_error, { lineId }, context) => {
      if (!context?.original) return;
      const original = context.original;
      queryClient.setQueryData<Month>(monthKey(hid, period), (current) => {
        if (!current) return current;
        const lines = [...current.lines];
        const index = lines.findIndex((line) => line.id === lineId);
        if (index >= 0) lines[index] = original;
        else lines.splice(context.index, 0, original);
        return { ...current, lines };
      });
    },
    onSuccess: async (saved) => {
      const key = monthKey(hid, period);
      // An older in-flight read must not overwrite the confirmed edit.
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<Month>(key, (previous) =>
        previous
          ? {
              ...previous,
              lines: previous.lines.map((line) => (line.id === saved.id ? saved : line)),
            }
          : previous,
      );
    },
    onSettled: (_saved, error, { scope }, context) => {
      context?.finish(error ? undefined : scope === 'this_and_future' ? 'household' : 'month');
    },
  });
}

export function useDeleteLine(hid: string, period: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...monthKey(hid, period), 'line-delete'],
    meta: { successMessage: 'Entry removed' },
    mutationFn: ({ lineId, scope }: { lineId: string; scope: EditScope }) =>
      api.delete<void>(`/households/${hid}/months/${period}/lines/${lineId}?scope=${scope}`),
    onMutate: async ({ lineId }) => {
      const finish = await acquireLineEdit(queryClient, hid, period, lineId);
      const key = monthKey(hid, period);
      try {
        await queryClient.cancelQueries({ queryKey: key });
      } catch (error) {
        finish();
        throw error;
      }
      const previous = queryClient.getQueryData<Month>(key);
      const original = previous?.lines.find((line) => line.id === lineId);
      const index = previous?.lines.findIndex((line) => line.id === lineId) ?? -1;
      if (previous)
        queryClient.setQueryData<Month>(key, {
          ...previous,
          lines: previous.lines.filter((line) => line.id !== lineId),
        });
      return { original, index, finish };
    },
    onError: (_error, { lineId }, context) => {
      if (!context?.original) return;
      const original = context.original;
      queryClient.setQueryData<Month>(monthKey(hid, period), (current) => {
        if (!current) return current;
        const lines = [...current.lines];
        const index = lines.findIndex((line) => line.id === lineId);
        if (index >= 0) lines[index] = original;
        else lines.splice(context.index, 0, original);
        return { ...current, lines };
      });
    },
    onSuccess: async (_data, { lineId }) => {
      const key = monthKey(hid, period);
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<Month>(key, (previous) =>
        previous
          ? { ...previous, lines: previous.lines.filter((line) => line.id !== lineId) }
          : previous,
      );
    },
    onSettled: (_saved, error, { scope }, context) => {
      context?.finish(error ? undefined : scope === 'this_and_future' ? 'household' : 'month');
    },
  });
}

export interface SetLineStatusInput {
  lineId: string;
  status: LineStatus;
  actualAmount?: number;
}

export function useSetLineStatus(hid: string, period: string) {
  const queryClient = useQueryClient();
  const pushToast = useToast();
  const key = monthKey(hid, period);

  const mutationKey = [...key, 'line-status'];
  const pendingLineIds = new Set(
    useMutationState({
      filters: { mutationKey, exact: true, status: 'pending' },
      select: (mutation) => (mutation.state.variables as SetLineStatusInput).lineId,
    }),
  );
  const mutation = useMutation<MonthLine, unknown, SetLineStatusInput>({
    mutationKey,
    meta: { celebrate: 'paid', feedback: 'payment' },
    mutationFn: ({ lineId, status, actualAmount }) =>
      api.post<MonthLine>(`/households/${hid}/months/${period}/lines/${lineId}/status`, {
        status,
        actualAmount,
      }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      // Keep the row and totals stable until the API confirms the write.
    },
    onSuccess: async (saved) => {
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<Month>(key, (previous) =>
        previous
          ? {
              ...previous,
              lines: previous.lines.map((line) => (line.id === saved.id ? saved : line)),
            }
          : previous,
      );
    },
    onError: (err) => {
      pushToast(monthErrorMessage(err), 'error');
    },
    onSettled: () => {
      // Refresh after the last overlapping write without extending its saving state.
      if (queryClient.isMutating({ mutationKey, exact: true }) === 1) {
        void queryClient.invalidateQueries({ queryKey: ['h', hid] });
      }
    },
  });
  return { ...mutation, pendingLineIds };
}

export function monthErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Something went wrong — try again.';
}

export function usePayRdInstalments(hid: string, period: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (_input: { status: 'paid' }) =>
      api.post<Month>(`/households/${hid}/months/${period}/rd-payments`),
    meta: {
      successMessage: 'RD auto-debits recorded',
      celebrate: 'paid',
      feedback: 'contribution',
    },
    onSuccess: (month) => client.setQueryData(monthKey(hid, period), month),
    onSettled: () => client.invalidateQueries({ queryKey: ['h', hid] }),
  });
}
